import type { InviteLookupModalProps } from '../../types/modals'
/**
 * Invite Look Up dialog — mirrors Angular `InviteLookupComponent`
 */
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { formatAmount } from '@/lib/utils/formatters'


const tid = 'application-modal-invite-lookup'

export function InviteLookupModal({
  isOpen,
  invites,
  onClose,
  onSelect,
}: InviteLookupModalProps) {
  const cleaned = invites.filter((r) => !!r.invite_code)
  const hasInvites = cleaned.length > 0

  const title = hasInvites
    ? 'An invitation has been found for you:'
    : 'No Invitation or previous Applications were found for this applicant'

  return (
    <OnScreenModal
      isOpen={isOpen}
      title="Invite"
      onClose={onClose}
      maxWidth="max-w-2xl"
      overlayDataTestId={tid}
      panelDataTestId={`${tid}-panel`}
      titleDataTestId={`${tid}-title`}
      closeButtonDataTestId={`${tid}-close-btn`}
    >
      <div className="space-y-3" data-testid={`${tid}-body`}>
        <p className="font-semibold text-slate-800" data-testid={`${tid}-subtitle`}>{title}</p>

        {!hasInvites ? (
          <div className="pt-2" data-testid={`${tid}-no-invites`}>
            <button
              type="button"
              onClick={onClose}
              data-testid={`${tid}-continue-btn`}
              className="px-5 py-2 rounded-lg bg-[rgb(21_151_255)] text-white text-sm font-semibold hover:bg-[#0d7acc]"
            >
              Continue
            </button>
          </div>
        ) : (
          <div className="space-y-3" data-testid={`${tid}-invite-list`}>
            {cleaned.map((invite, idx) => (
              <div
                key={`${invite.invite_code}-${idx}`}
                data-testid={`${tid}-row-${invite.invite_code}`}
                className="rounded-md border border-slate-200 bg-white p-4"
              >
                <h6 className="text-sm font-semibold text-slate-900 mb-3" data-testid={`${tid}-row-${invite.invite_code}-code`}>
                  Invitation Code: {invite.invite_code}
                </h6>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div data-testid={`${tid}-row-${invite.invite_code}-property`}>
                    <p className="text-xs font-semibold text-slate-500">Property</p>
                    <p className="text-sm text-slate-800">
                      {invite.property_name || '—'}
                    </p>
                  </div>
                  <div data-testid={`${tid}-row-${invite.invite_code}-agent`}>
                    <p className="text-xs font-semibold text-slate-500">
                      Leasing Consultant
                    </p>
                    <p className="text-sm text-slate-800">
                      {invite.agent_name || '—'}
                    </p>
                  </div>
                  <div data-testid={`${tid}-row-${invite.invite_code}-rent`}>
                    <p className="text-xs font-semibold text-slate-500">Rent Amount</p>
                    <p className="text-sm text-slate-800">
                      {invite.rent_amount != null && invite.rent_amount !== ''
                        ? formatAmount(Number(invite.rent_amount))
                        : '—'}
                    </p>
                    </div>
                  {invite.primary_user && (
                    <div data-testid={`${tid}-row-${invite.invite_code}-primary`}>
                      <p className="text-xs font-semibold text-slate-500">
                        Primary Applicant
                      </p>
                      <p className="text-sm text-slate-800">{invite.primary_user}</p>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onSelect(invite.invite_code as string)}
                  data-testid={`${tid}-row-${invite.invite_code}-continue-btn`}
                  className="px-5 py-2 rounded-lg bg-[rgb(21_151_255)] text-white text-sm font-semibold hover:bg-[#0d7acc]"
                >
                  Continue
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </OnScreenModal>
  )
}
